import { Store } from '@ngrx/store';
import Dexie, { Table, liveQuery } from 'dexie';
import { State } from '../../shared/core/state';
import { Injectable, inject } from '@angular/core';
import xxhash from 'xxhash-wasm';

import * as CourseActions from '../../shared/core/state/course/course.actions';

interface CourseItem {
  id?: number;
  title: string;
  contents: CourseContent[];
  courseId?: string;
}

interface CourseContent {
  id: string;
  title: string;
  resourceId?: string;
  url?: string;
  type?: string;
  isFreePreview?: boolean;
}

class CourseCreatorDB extends Dexie {
  courseItems!: Table<CourseItem, number>;

  constructor() {
    super('CourseCreator');
    this.version(1).stores({ courseItems: '++id, courseId' });
  }
}

@Injectable()
export default class CourseCreatorService {
  private store = inject<Store<State>>(Store);

  courseId = '';
  courseItems: CourseItem[] = [];
  db;

  constructor() {
    this.db = new CourseCreatorDB();
  }

  async pushToServer() {
    if (this.courseId !== '') {
      this.courseItems = await this.db.courseItems
        .where({ courseId: this.courseId })
        .toArray();

      this.store.dispatch(
        CourseActions.syncCourseContents({
          courseId: this.courseId,
          courseContents: this.courseItems.map((item) => {
            return {
              topic: item.title,
              resources: item.contents.map((content) => {
                return {
                  _id: content.id,
                  title: content.title,
                  resource: content.resourceId || undefined,
                  url: content.url || undefined,
                  type: content.type || 'video',
                  isFreePreview: content.isFreePreview || false,
                };
              }),
            };
          }),
        })
      );
    }
  }

  async pullFromState(courseItemsFromState: CourseItem[]) {
    await this.bulkAddCourseItem(courseItemsFromState);
  }

  async checkConflict(courseItemsFromState: CourseItem[]) {
    this.courseItems = await this.db.courseItems
      .where({ courseId: this.courseId })
      .toArray();

    const _courseItem = this.courseItems.map((item) => {
      return {
        title: item.title,
        contents: item.contents.map((content) => {
          return {
            title: content.title,
            resourceId: content.resourceId,
          };
        }),
      };
    });

    const _courseItemsFromState = courseItemsFromState.map((item) => {
      return {
        title: item.title,
        contents: item.contents.map((content) => {
          return {
            title: content.title,
            resourceId: content.resourceId,
          };
        }),
      };
    });

    const { h32 } = await xxhash();
    const stateDocHash = h32(JSON.stringify(_courseItemsFromState));
    const localDocHash = h32(JSON.stringify(_courseItem));

    return stateDocHash != localDocHash;
  }

  async flushData() {
    return await this.db.courseItems
      .where({ courseId: this.courseId })
      .delete();
  }

  setCourseId(id: string) {
    this.courseId = id;
  }

  addCourseItem(courseItem: CourseItem) {
    courseItem.courseId = this.courseId;
    return this.db.courseItems.add(courseItem);
  }

  deleteCourseItem(itemId: number) {
    return this.db.courseItems.delete(itemId);
  }

  bulkAddCourseItem(courseItems: CourseItem[]) {
    const updatedCourseItems = courseItems.map((item) => {
      return { ...item, courseId: this.courseId };
    });
    return this.db.courseItems.bulkAdd(updatedCourseItems);
  }

  getCourseItem(id: number) {
    return this.db.courseItems.get(id);
  }

  getCourseItemsByCourseId(courseId: string) {
    return liveQuery(() => this.db.courseItems.where({ courseId }).toArray());
  }

  removeCourseItem() {
    return 0;
  }

  // add or update course content
  async updateCourseContent(
    courseItemId: number,
    courseContent: CourseContent
  ) {
    const selectedCourseItem = await this.db.courseItems.get(courseItemId);

    if (!selectedCourseItem) return -1;

    const selectedCourseContentIndex = selectedCourseItem?.contents.findIndex(
      (content) => content.id == courseContent.id
    );

    if (selectedCourseItem && selectedCourseContentIndex !== -1) {
      selectedCourseItem.contents[selectedCourseContentIndex] = courseContent;
    } else {
      selectedCourseItem?.contents.push(courseContent);
    }

    return await this.db.courseItems.put(selectedCourseItem);
  }

  async removeCourseContent(courseItemId: number, courseContentId: string) {
    const selectedCourseItem = await this.db.courseItems.get(courseItemId);
    if (!selectedCourseItem) return -1;

    const selectedCourseContentIndex = selectedCourseItem?.contents.findIndex(
      (content) => content.id == courseContentId
    );

    if (selectedCourseItem && selectedCourseContentIndex !== -1) {
      selectedCourseItem.contents.splice(selectedCourseContentIndex, 1);
    }

    return await this.db.courseItems.put(selectedCourseItem);
  }
}
